// src/pages/admin/CourseLectures/lectures/FSJLecture03Application.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-4 rounded-2xl border overflow-hidden shadow-sm ${
        isOpen ? "border-[#009fe3]/50" : "border-gray-200"
      } bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${
          isOpen ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-colors ${
            isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"
          }`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-base font-bold ${
            isOpen ? "text-[#0369a1]" : "text-gray-800"
          }`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-3 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Doc Checklist Item ────────────────────────────────────────────
const DocItem: React.FC<{
  icon: string;
  title: string;
  desc: string;
  required?: boolean;
  color: string;
}> = ({ icon, title, desc, required = true, color }) => (
  <div className={`rounded-2xl border p-4 ${color}`}>
    <div className="flex items-start gap-3">
      <span className="text-2xl flex-shrink-0">{icon}</span>
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="text-sm font-bold">{title}</p>
          <span
            className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              required ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"
            }`}
          >
            {required ? "Pflicht" : "Optional"}
          </span>
        </div>
        <p className="text-sm opacity-80">{desc}</p>
      </div>
    </div>
  </div>
);

// ── Träger Card ───────────────────────────────────────────────────
const TraegerCard: React.FC<{
  icon: string;
  name: string;
  examples: string[];
  tip: string;
  color: string;
  accomm?: boolean;
}> = ({ icon, name, examples, tip, color, accomm }) => (
  <div className={`rounded-2xl border p-4 ${color}`}>
    <div className="flex items-center gap-2 mb-2">
      <span className="text-xl">{icon}</span>
      <p className="text-sm font-bold">{name}</p>
      {accomm && (
        <span className="ml-auto text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
          🏠 Often includes housing
        </span>
      )}
    </div>
    <div className="space-y-1 mb-3">
      {examples.map((e, i) => (
        <div
          key={i}
          className="flex items-center gap-2 bg-white/60 rounded-xl px-3 py-1.5"
        >
          <span className="font-bold opacity-50 flex-shrink-0">›</span>
          <p className="text-sm">{e}</p>
        </div>
      ))}
    </div>
    <div className="bg-white/50 rounded-xl px-3 py-2 border border-current/10">
      <p className="text-xs font-bold opacity-60 uppercase tracking-wide mb-0.5">
        💡 Tip
      </p>
      <p className="text-sm">{tip}</p>
    </div>
  </div>
);

// ── Email Block ───────────────────────────────────────────────────
const EmailBlock: React.FC<{
  subject: string;
  body: string;
}> = ({ subject, body }) => (
  <div className="rounded-2xl border border-gray-300 overflow-hidden font-mono text-sm">
    <div className="bg-gray-100 border-b border-gray-300 px-4 py-2">
      <p className="text-xs text-gray-500 font-sans font-semibold uppercase tracking-wide">
        ✉️ E-Mail Vorlage
      </p>
    </div>
    <div className="bg-white px-4 py-3 border-b border-gray-200">
      <span className="text-xs font-sans font-bold text-gray-400 uppercase tracking-wide mr-2">
        Betreff:
      </span>
      <span className="text-sm text-gray-800 font-semibold">{subject}</span>
    </div>
    <div className="bg-white px-4 py-4">
      <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed font-mono">
        {body}
      </pre>
    </div>
  </div>
);

// ── Tip Pill ──────────────────────────────────────────────────────
const TipPill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
    <span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">💡</span>
    <p className="text-sm text-amber-800">{children}</p>
  </div>
);

// ── Link Resource ─────────────────────────────────────────────────
const LinkResource: React.FC<{
  icon: string;
  title: string;
  url: string;
  desc: string;
  badge?: string;
  badgeColor?: string;
}> = ({
  icon,
  title,
  url,
  desc,
  badge,
  badgeColor = "bg-[#009fe3] text-white",
}) => (
  <div className="bg-white rounded-xl border border-blue-200 px-4 py-3">
    <div className="flex items-start gap-3">
      <span className="text-xl flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <p className="text-sm font-bold text-blue-800">{title}</p>
          {badge && (
            <span
              className={`text-xs font-bold px-2 py-0.5 rounded-full ${badgeColor}`}
            >
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs text-blue-600 break-all mb-1">{url}</p>
        <p className="text-sm text-gray-600">{desc}</p>
      </div>
    </div>
  </div>
);

// ── Cover Letter Section Block ────────────────────────────────────
const LetterSection: React.FC<{
  num: string;
  title: string;
  content: string;
  example: string;
  color: string;
}> = ({ num, title, content, example, color }) => (
  <div className={`rounded-2xl border p-4 ${color}`}>
    <div className="flex items-center gap-2 mb-2">
      <span className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-sm font-extrabold flex-shrink-0">
        {num}
      </span>
      <p className="text-sm font-bold">{title}</p>
    </div>
    <p className="text-sm opacity-80 mb-3">{content}</p>
    <div className="bg-white/60 rounded-xl px-4 py-3 border-l-4 border-current/30">
      <p className="text-xs font-bold opacity-50 uppercase tracking-wide mb-1">
        Beispiel:
      </p>
      <p className="text-sm italic opacity-90">{example}</p>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const FSJLecture03Application: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  const emailBody = `Sehr geehrte Damen und Herren,

hiermit sende ich Ihnen meine Bewerbungsunterlagen für ein
Freiwilliges Soziales Jahr in Ihrer Einrichtung.

Im Anhang finden Sie:
  • Motivationsschreiben
  • Lebenslauf
  • Schulzeugnisse (mit beglaubigter Übersetzung)
  • Sprachzertifikat (Goethe-Institut, Niveau A2/B1)

Ich freue mich sehr auf Ihre Rückmeldung und stehe für
Rückfragen jederzeit gerne zur Verfügung.

Mit freundlichen Grüßen,
[Ihr Name]
[Telefonnummer / WhatsApp]
[E-Mail-Adresse]`;

  const emailBFDBody = `Sehr geehrte Damen und Herren,

ich interessiere mich sehr für einen Platz im
Bundesfreiwilligendienst (BFD) in Ihrer Einrichtung
im Bereich [Pflege / Kinder / Umwelt — anpassen].

Im Anhang finden Sie meine vollständigen Bewerbungsunterlagen:
  • Motivationsschreiben
  • Lebenslauf
  • Schulzeugnisse
  • Sprachzertifikat

Für Rückfragen stehe ich jederzeit zur Verfügung.

Mit freundlichen Grüßen,
[Ihr Name]
[Kontaktdaten]`;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Course Header Banner */}
      <div className="bg-gradient-to-r from-[#009fe3] to-[#0369a1] rounded-2xl px-6 py-5 mb-5">
        <p className="text-xs font-bold uppercase tracking-widest text-white opacity-70 mb-1">
          FSJ / BFD Preparatory Course
        </p>
        <p className="text-xl font-extrabold text-white">
          Session 03 — The Application: Documents, Platforms & Strategy
        </p>
        <p className="text-sm text-white opacity-80 mt-1">
          Designed by Guideline2Germany
        </p>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Documents You Need — Full Checklist" },
          {
            num: "2",
            label: "The Motivationsschreiben — Structure & Examples",
          },
          { num: "3", label: "Email Format — How to Send Your Application" },
          { num: "4", label: "Where to Find Placements — Träger & Platforms" },
          { num: "5", label: "With or Without Accommodation — What to Know" },
          { num: "6", label: "Tips for a Better Chance — Insider Strategies" },
        ]}
      />

      {/* 01 — Documents */}
      <Accordion
        id="01"
        num="SECTION 01"
        icon="📁"
        title="Documents You Need — Full Checklist"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-2xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4 mb-5">
          <p className="text-base font-bold text-[#0369a1] mb-1">
            Every document must be ready before you start applying.
          </p>
          <p className="text-sm text-[#0369a1]">
            Missing even one document can cause delays of weeks. Prepare
            everything in advance — digital and printed copies.
          </p>
        </div>

        <div className="space-y-3 mb-5">
          <DocItem
            icon="✍️"
            title="Motivationsschreiben (Cover Letter)"
            desc="A personal letter explaining who you are, why you want to do FSJ/BFD, and what you offer the organization. 1 page maximum. In German."
            color="bg-blue-50 border-blue-200 text-blue-800"
          />
          <DocItem
            icon="📄"
            title="Lebenslauf (CV / Résumé)"
            desc="Tabular CV in German — education, work/volunteer experience, language skills. Use a clean European format (no photo required but common). In German."
            color="bg-purple-50 border-purple-200 text-purple-800"
          />
          <DocItem
            icon="🎓"
            title="Schulzeugnis (School Certificate)"
            desc="Your SSC and/or HSC certificates. Must include a certified German or English translation. Attested by a sworn translator or official authority."
            color="bg-amber-50 border-amber-200 text-amber-800"
          />
          <DocItem
            icon="🗣️"
            title="Sprachzertifikat (German Language Certificate)"
            desc="Goethe-Institut A2 or B1 certificate. This is the single most important document after the Motivationsschreiben. Strongly differentiates your application."
            color="bg-green-50 border-green-200 text-green-800"
          />
          <DocItem
            icon="🪪"
            title="Passport Copy"
            desc="Valid passport — minimum 18 months validity beyond your planned start date. Full scan of biographical page."
            color="bg-red-50 border-red-200 text-red-800"
          />
          <DocItem
            icon="📸"
            title="Passport Photo (biometric)"
            desc="Recent biometric-style photo. Some organizations request this with the application; always required for the visa."
            color="bg-indigo-50 border-indigo-200 text-indigo-800"
          />
          <DocItem
            icon="💌"
            title="Referenzschreiben (Reference Letter)"
            desc="Optional but very helpful — a letter from a teacher, employer, or community leader confirming your character and reliability."
            required={false}
            color="bg-gray-50 border-gray-200 text-gray-700"
          />
          <DocItem
            icon="🏥"
            title="Health Certificate / Erklärung"
            desc="Some organizations — especially hospitals and elderly care — require a basic medical fitness declaration. Check with each Träger individually."
            required={false}
            color="bg-teal-50 border-teal-200 text-teal-800"
          />
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-bold text-amber-800 mb-2">
            ⚠️ Translation Rules
          </p>
          <div className="space-y-1.5">
            {[
              "All Bangladeshi documents must be translated into German or English",
              "Translations must be done by a sworn/certified translator (beeidigter Übersetzer)",
              "German embassies do not accept uncertified Google Translate copies",
              "Keep both originals and certified copies — never send the only original",
            ].map((item, i) => (
              <p
                key={i}
                className="text-sm text-amber-700 flex items-start gap-2"
              >
                <span className="flex-shrink-0 font-bold">›</span> {item}
              </p>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 02 — Motivationsschreiben */}
      <Accordion
        id="02"
        num="SECTION 02"
        icon="✍️"
        title="The Motivationsschreiben — Structure & Examples"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 mb-5">
          <p className="text-base font-bold text-blue-800 mb-1">
            This letter is the heart of your application.
          </p>
          <p className="text-sm text-blue-700">
            Organizations receive many applications. Your Motivationsschreiben
            is what makes them remember <em>you</em>. It must be personal,
            specific, and honest — not a generic template.
          </p>
        </div>

        {/* Format rules */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {[
            {
              icon: "📏",
              label: "Length",
              value: "Max. 1 page (A4)",
              color: "bg-gray-50 border-gray-200",
            },
            {
              icon: "🇩🇪",
              label: "Language",
              value: "In German (always)",
              color: "bg-blue-50 border-blue-200",
            },
            {
              icon: "🖋️",
              label: "Format",
              value: "DIN 5008 letter style",
              color: "bg-purple-50 border-purple-200",
            },
          ].map((item, i) => (
            <div
              key={i}
              className={`rounded-2xl border p-3 text-center ${item.color}`}
            >
              <span className="text-2xl block mb-1">{item.icon}</span>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wide">
                {item.label}
              </p>
              <p className="text-sm font-bold text-gray-800 mt-0.5">
                {item.value}
              </p>
            </div>
          ))}
        </div>

        {/* Structure */}
        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
          Structure — 5 Paragraphs
        </p>
        <div className="space-y-3 mb-5">
          <LetterSection
            num="1"
            title="Opening — Who are you and what are you applying for?"
            content="State your name, nationality, and exactly which position you are applying for. Be specific about the organization and the role. Never use a generic opener."
            example="Mein Name ist Sumaiya Akter, ich komme aus Bangladesch und bewerbe mich hiermit für einen FSJ-Platz in der Abteilung Altenpflege bei der Caritas München."
            color="bg-blue-50 border-blue-200 text-blue-800"
          />
          <LetterSection
            num="2"
            title="Motivation — Why this program and why Germany?"
            content="Explain your genuine motivation. Why FSJ/BFD specifically? Why Germany? What drew you to this sector? Avoid clichés like 'I love helping people' — be specific and personal."
            example="Seit meiner Kindheit pflege ich kranke Familienmitglieder. In Deutschland möchte ich professionelles Pflegewissen erwerben und gleichzeitig die Sprache und Kultur vertiefen."
            color="bg-purple-50 border-purple-200 text-purple-800"
          />
          <LetterSection
            num="3"
            title="What you offer — Your skills and experience"
            content="Describe relevant experience: childcare, tutoring, volunteering, or caring for family members. Mention your German level and any other strengths. Be honest — organizations value reliability over perfection."
            example="Ich habe zwei Jahre lang jüngere Geschwister betreut und verfüge über grundlegende Deutschkenntnisse auf dem Niveau A2 (Goethe-Institut). Ich bin zuverlässig, lernbereit und teamfähig."
            color="bg-amber-50 border-amber-200 text-amber-800"
          />
          <LetterSection
            num="4"
            title="Future goal — What you want to learn and become"
            content="Show that you have a clear vision beyond the FSJ/BFD. This is crucial — organizations want volunteers who are committed and goal-oriented, not just looking for a visa route."
            example="Nach dem FSJ möchte ich eine Ausbildung zur Pflegefachfrau beginnen. Das FSJ ist für mich der erste Schritt in eine langfristige Karriere im deutschen Gesundheitswesen."
            color="bg-green-50 border-green-200 text-green-800"
          />
          <LetterSection
            num="5"
            title="Closing — Professional sign-off"
            content="Thank the organization for their time, express enthusiasm for the opportunity, and invite them to contact you. Keep it professional and warm."
            example="Ich freue mich sehr über die Möglichkeit, mich persönlich vorzustellen, und stehe für Rückfragen jederzeit zur Verfügung. Mit freundlichen Grüßen, Sumaiya Akter"
            color="bg-indigo-50 border-indigo-200 text-indigo-800"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-bold text-green-800 mb-2">
              ✅ Always Do
            </p>
            <div className="space-y-1.5">
              {[
                "Mention the organization by name",
                "Be specific about which sector (Pflege, Kinder, Umwelt)",
                "Include your Goethe certificate level",
                "Show a clear career goal after FSJ/BFD",
                "Proofread — use DeepL or a German speaker",
              ].map((item, i) => (
                <p
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 font-bold text-green-500">
                    ✓
                  </span>{" "}
                  {item}
                </p>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-bold text-red-800 mb-2">❌ Never Do</p>
            <div className="space-y-1.5">
              {[
                "Use a copy-paste generic template",
                "Write in English or Bengali",
                "Make it longer than one A4 page",
                "Say 'I want to go to Germany' as a motivation",
                "Leave out your German language level",
              ].map((item, i) => (
                <p
                  key={i}
                  className="text-sm text-red-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 font-bold text-red-500">
                    ✗
                  </span>{" "}
                  {item}
                </p>
              ))}
            </div>
          </div>
        </div>
      </Accordion>

      {/* 03 — Email Format */}
      <Accordion
        id="03"
        num="SECTION 03"
        icon="✉️"
        title="Email Format — How to Send Your Application"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mb-5">
          <p className="text-base font-bold text-amber-800 mb-1">
            The email itself is part of the application.
          </p>
          <p className="text-sm text-amber-700">
            A messy, informal email will harm your chances before they even open
            your documents. Keep it short, professional, and structured.
          </p>
        </div>

        {/* Rules */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            {
              icon: "📎",
              label: "Attachments",
              value: "PDF only — never .docx",
            },
            { icon: "📏", label: "Email body", value: "5–8 lines maximum" },
            { icon: "🇩🇪", label: "Language", value: "German always" },
            {
              icon: "📂",
              label: "File names",
              value: "Nachname_Vorname_CV.pdf",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="rounded-2xl border border-gray-200 bg-gray-50 p-3 text-center"
            >
              <span className="text-xl block mb-1">{item.icon}</span>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wide">
                {item.label}
              </p>
              <p className="text-sm font-bold text-gray-800 mt-0.5">
                {item.value}
              </p>
            </div>
          ))}
        </div>

        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
          FSJ Application Email
        </p>
        <div className="mb-5">
          <EmailBlock
            subject="Bewerbung FSJ — [Ihr Name] — [Einrichtung, z.B. Caritas Pflegeheim München]"
            body={emailBody}
          />
        </div>

        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
          BFD Application Email
        </p>
        <div className="mb-5">
          <EmailBlock
            subject="Bewerbung BFD — [Ihr Name] — [Einrichtung]"
            body={emailBFDBody}
          />
        </div>

        <div className="space-y-2">
          <TipPill>
            <strong>File naming matters.</strong> Name your PDF files like this:
            Akter_Sumaiya_Motivationsschreiben.pdf — makes it easy for the
            coordinator to organize your documents.
          </TipPill>
          <TipPill>
            <strong>Send a follow-up after 2 weeks</strong> if you receive no
            reply. One short, polite email: "Ich wollte höflich nachfragen, ob
            meine Bewerbung angekommen ist." Many placements are filled through
            follow-ups.
          </TipPill>
          <TipPill>
            <strong>Apply to 10–15 organizations simultaneously.</strong>{" "}
            Competition is high. Spread applications across different cities and
            sectors to maximize your chances.
          </TipPill>
        </div>
      </Accordion>

      {/* 04 — Träger & Platforms */}
      <Accordion
        id="04"
        num="SECTION 04"
        icon="🏢"
        title="Where to Find Placements — Träger & Platforms"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-2xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4 mb-5">
          <p className="text-base font-bold text-[#0369a1] mb-1">
            What is a Träger?
          </p>
          <p className="text-sm text-[#0369a1]">
            A <strong>Träger</strong> (carrier/provider) is the organization
            that officially manages your FSJ/BFD. They handle your contract,
            seminars, and legal paperwork. The Einsatzstelle (deployment site)
            is where you physically work — sometimes the same as the Träger,
            sometimes different.
          </p>
        </div>

        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
          Major Träger Types — Where Can You Work?
        </p>
        <div className="space-y-4 mb-6">
          <TraegerCard
            icon="⛪"
            name="Caritas & Diakonie (Church Welfare Organizations)"
            examples={[
              "Elderly care homes (Altenheime) and nursing stations",
              "Hospitals and rehabilitation centers",
              "Children and youth social services",
              "Disability support facilities",
            ]}
            tip="Caritas (Catholic) and Diakonie (Protestant) are the two largest FSJ employers in Germany. They have hundreds of open placements across all states. Many include accommodation."
            color="bg-blue-50 border-blue-200 text-blue-800"
            accomm
          />
          <TraegerCard
            icon="🏛️"
            name="City / Municipal Träger (Städtische Träger)"
            examples={[
              "City-run kindergartens (städtische Kitas)",
              "Municipal hospitals and health centers",
              "Public libraries and cultural institutions",
              "City youth welfare offices (Jugendamt)",
            ]}
            tip="City Träger are highly stable, well-organized, and often have strong support structures for international volunteers. Search directly on each city's official website."
            color="bg-teal-50 border-teal-200 text-teal-800"
            accomm
          />
          <TraegerCard
            icon="🌿"
            name="AWO / DRK / DPWV (Secular Welfare Organizations)"
            examples={[
              "AWO: Arbeiterwohlfahrt — social and youth work",
              "DRK: Deutsches Rotes Kreuz — emergency and care services",
              "DPWV: Paritätischer Wohlfahrtsverband — diverse social work",
            ]}
            tip="AWO and DRK are among the most international-friendly organizations. DRK placements often come with structured German language support during service."
            color="bg-rose-50 border-rose-200 text-rose-800"
            accomm
          />
          <TraegerCard
            icon="🌍"
            name="Environmental & Nature Conservation (BFD focus)"
            examples={[
              "NABU and BUND (nature conservation associations)",
              "National parks and Biosphere reserves",
              "Environmental education centers",
              "Urban gardening and climate action projects",
            ]}
            tip="Only available through BFD. Ideal if you want outdoor work and are interested in environmental topics. Often located in smaller towns — important for accommodation planning."
            color="bg-green-50 border-green-200 text-green-800"
          />
          <TraegerCard
            icon="⛹️"
            name="Sports & Cultural Institutions"
            examples={[
              "Sports clubs (Sportvereine) — coaching, administration",
              "Youth hostels (Jugendherbergen) — hospitality and events",
              "Museums and cultural centers",
              "Community theaters and music schools",
            ]}
            tip="Jugendherbergen (DJH youth hostels) are a popular and well-organized placement type — they operate nationwide, often provide accommodation, and have international experience with volunteers."
            color="bg-amber-50 border-amber-200 text-amber-800"
            accomm
          />
        </div>

        {/* Online Platforms */}
        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
          🔗 Best Platforms to Find Placements
        </p>
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 space-y-3">
          <LinkResource
            icon="🌐"
            title="freiwillig-ja.de"
            url="https://freiwillig-ja.de/"
            desc="One of the best dedicated FSJ/BFD search portals. Filter by state, sector, and start date. Used by hundreds of Träger across Germany."
            badge="Top Platform"
            badgeColor="bg-[#009fe3] text-white"
          />
          <LinkResource
            icon="🏛️"
            title="BMFSFJ Federal Database"
            url="https://www.bundesfreiwilligendienst.de"
            desc="Official federal BFD platform run by the government. Comprehensive list of all registered BFD placement organizations."
            badge="Official"
            badgeColor="bg-gray-700 text-white"
          />
          <LinkResource
            icon="🤝"
            title="Caritas FSJ Portal"
            url="https://www.caritas.de/stellen/fsj"
            desc="Direct FSJ application portal for all Caritas organizations across Germany. One of the largest single providers."
            badge="Largest Provider"
            badgeColor="bg-red-600 text-white"
          />
          <LinkResource
            icon="❤️"
            title="Diakonie FSJ / BFD"
            url="https://www.diakonie.de/freiwilligendienste"
            desc="Diakonie's own volunteer services portal. Covers FSJ and BFD placements in Protestant welfare organizations."
            badge="Large Provider"
            badgeColor="bg-orange-600 text-white"
          />
          <LinkResource
            icon="🔴"
            title="DRK Freiwilligendienste"
            url="https://www.drk.de/engagement/freiwilligendienste/"
            desc="German Red Cross FSJ and BFD placement portal. International-friendly with strong organizational support."
            badge="DRK"
            badgeColor="bg-red-700 text-white"
          />
          <LinkResource
            icon="⛹️"
            title="DJH Jugendherberge FSJ"
            url="https://www.jugendherberge.de/ueber-uns/jobs-praktika/"
            desc="Youth hostel association — nationwide, well-organized, often provides accommodation. Great option for social and hospitality-oriented applicants."
            badge="Accommodation Often Included"
            badgeColor="bg-green-600 text-white"
          />
          <LinkResource
            icon="🔍"
            title="Google Search Strategy"
            url='Stadt [Stadtname] FSJ Stelle — e.g. "Stadt München FSJ Stelle 2025"'
            desc="Search directly for city Träger. Many cities post FSJ/BFD placements on their own official websites — not listed on the national portals."
            badge="Pro Tip"
            badgeColor="bg-amber-500 text-white"
          />
        </div>
      </Accordion>

      {/* 05 — Accommodation */}
      <Accordion
        id="05"
        num="SECTION 05"
        icon="🏠"
        title="With or Without Accommodation — What to Know"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mb-5">
          <p className="text-base font-bold text-amber-800 mb-1">
            Accommodation is one of the most important practical decisions.
          </p>
          <p className="text-sm text-amber-700">
            Germany has a housing shortage — especially in larger cities. Your
            accommodation status directly affects your budget, your city choice,
            and how quickly you can start.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          {/* With accommodation */}
          <div className="rounded-2xl border-2 border-green-300 bg-green-50 p-4">
            <p className="text-base font-bold text-green-800 mb-3 flex items-center gap-2">
              🏠 With Accommodation (Unterkunft inklusive)
            </p>
            <div className="space-y-2 mb-4">
              {[
                "Provided directly by the organization or Träger",
                "Often a shared room in a volunteer house (WG or Wohnheim)",
                "Cost deducted from pocket money — typically 100–200 €/month",
                "No need to search for a flat independently",
                "More social — you live with other volunteers",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-xl px-3 py-2"
                >
                  <span className="text-green-500 font-bold flex-shrink-0">
                    ✓
                  </span>
                  <p className="text-sm text-green-800">{item}</p>
                </div>
              ))}
            </div>
            <div className="bg-green-100 rounded-xl px-3 py-2 border border-green-300">
              <p className="text-xs font-bold text-green-700 mb-1">Best For:</p>
              <p className="text-sm text-green-700">
                First-time international volunteers, those arriving without a
                local network, anyone coming to a large city where renting
                independently is very difficult.
              </p>
            </div>
          </div>

          {/* Without accommodation */}
          <div className="rounded-2xl border-2 border-orange-300 bg-orange-50 p-4">
            <p className="text-base font-bold text-orange-800 mb-3 flex items-center gap-2">
              🔑 Without Accommodation (Unterkunft selbst suchen)
            </p>
            <div className="space-y-2 mb-4">
              {[
                "You find and rent your own flat or room (WG — Wohngemeinschaft)",
                "Full pocket money — no deduction",
                "More independence and freedom in location choice",
                "Can be very challenging in Munich, Berlin, Frankfurt",
                "Easier in smaller cities like Erfurt, Magdeburg, Chemnitz",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-xl px-3 py-2"
                >
                  <span className="text-orange-500 font-bold flex-shrink-0">
                    ›
                  </span>
                  <p className="text-sm text-orange-800">{item}</p>
                </div>
              ))}
            </div>
            <div className="bg-orange-100 rounded-xl px-3 py-2 border border-orange-300">
              <p className="text-xs font-bold text-orange-700 mb-1">
                Best For:
              </p>
              <p className="text-sm text-orange-700">
                Those with contacts or family in Germany, or applicants
                targeting smaller East German cities where rents are affordable
                on a volunteer pocket money budget.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 mb-4">
          <p className="text-sm font-bold text-blue-800 mb-3">
            📊 Budget Reality Check — Monthly Breakdown
          </p>
          <div className="space-y-2">
            {[
              {
                label: "Pocket money (gross)",
                amount: "~500 €",
                note: "Typical FSJ/BFD rate",
                color: "text-green-700 font-bold",
              },
              {
                label: "Accommodation (if provided by Träger)",
                amount: "− 150 €",
                note: "Deducted from pocket money",
                color: "text-red-600",
              },
              {
                label: "Food (Mensa / supermarket)",
                amount: "− 100 €",
                note: "Estimated, varies by habits",
                color: "text-red-600",
              },
              {
                label: "Public transport (Monatskarte)",
                amount: "− 49 €",
                note: "Deutschlandticket (subsidized)",
                color: "text-red-600",
              },
              {
                label: "Language school",
                amount: "− 50–100 €",
                note: "Monthly contribution",
                color: "text-red-600",
              },
              {
                label: "Remaining / savings",
                amount: "~100–150 €",
                note: "Realistic net per month",
                color: "text-blue-700 font-bold",
              },
            ].map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-3 gap-2 bg-white rounded-xl px-3 py-2 ${
                  i === 5 ? "border-2 border-blue-300" : ""
                }`}
              >
                <p className="text-sm text-gray-700 col-span-1">{row.label}</p>
                <p className={`text-sm text-right ${row.color}`}>
                  {row.amount}
                </p>
                <p className="text-xs text-gray-400 self-center">{row.note}</p>
              </div>
            ))}
          </div>
        </div>

        <TipPill>
          <strong>East Germany strategy:</strong> Cities like Leipzig, Dresden,
          Erfurt, Halle, and Chemnitz have lower living costs and plenty of
          FSJ/BFD placements. Finding your own room is realistic on a volunteer
          budget here — unlike Munich or Hamburg.
        </TipPill>
      </Accordion>

      {/* 06 — Insider Tips */}
      <Accordion
        id="06"
        num="SECTION 06"
        icon="🎯"
        title="Tips for a Better Chance — Insider Strategies"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-2xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4 mb-5">
          <p className="text-base font-bold text-[#0369a1] mb-1">
            These are the things most applicants don't know.
          </p>
          <p className="text-sm text-[#0369a1]">
            Following these strategies significantly improves your placement
            success rate and reduces your waiting time.
          </p>
        </div>

        <div className="space-y-3 mb-5">
          {[
            {
              icon: "📅",
              title: "Apply 6–12 Months in Advance",
              desc: "German organizations plan early. September placements are filled by January–March. Do not start applying 2 months before your desired start date.",
              color: "bg-red-50 border-red-200 text-red-800",
            },
            {
              icon: "🏙️",
              title: "Target Smaller Cities First",
              desc: "Munich and Berlin are oversubscribed. Smaller cities (Kassel, Erfurt, Würzburg, Freiburg, Münster) have more openings and higher acceptance rates for international applicants.",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              icon: "📮",
              title: "Apply Directly — Not Only Through Portals",
              desc: "Many organizations list their own placements on their website before updating freiwillig-ja.de. Go directly to Caritas, Diakonie, and city websites and apply by email.",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "🎖️",
              title: "B1 Certificate = Major Advantage",
              desc: "Most international applicants apply with A2. Arriving with a B1 certificate immediately puts you in a different bracket. It signals genuine commitment and readiness.",
              color: "bg-green-50 border-green-200 text-green-800",
            },
            {
              icon: "📞",
              title: "Call Before & After Applying — Seriously",
              desc: "After sending your email, call the organization 1 week later. Ask: 'Ich wollte fragen, ob meine Bewerbung angekommen ist.' Germans respect initiative. Most applicants never call.",
              color: "bg-amber-50 border-amber-200 text-amber-800",
            },
            {
              icon: "📋",
              title: "Mention Childcare or Care Experience Explicitly",
              desc: "Even informal experience — babysitting siblings, helping at a community center, tutoring — is valuable. Name it specifically in your Motivationsschreiben.",
              color: "bg-indigo-50 border-indigo-200 text-indigo-800",
            },
            {
              icon: "🏠",
              title: "Prioritize Accommodation-Included Placements First",
              desc: "As an international applicant, securing placement with accommodation removes the biggest practical barrier. Only look at independent-housing placements as a second tier.",
              color: "bg-teal-50 border-teal-200 text-teal-800",
            },
            {
              icon: "🌐",
              title: "Search in German — Not in English",
              desc: 'Use German search terms: "FSJ Stelle Köln 2025", "BFD Altenheim Bayern", "FSJ Träger Hamburg Bewerbung". English searches return almost no relevant German results.',
              color: "bg-rose-50 border-rose-200 text-rose-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-2xl border p-4 ${item.color}`}>
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-base font-bold mb-1">{item.title}</p>
                  <p className="text-sm opacity-80">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <InfoBox
          variant="tip"
          icon="🔑"
          title="The Single Biggest Success Factor"
        >
          Applications with a <strong>Goethe B1 certificate</strong> + a{" "}
          <strong>personalized Motivationsschreiben</strong> that names the
          specific organization have a dramatically higher acceptance rate than
          generic applications. These two things are within your control — start
          working on them today.
        </InfoBox>
      </Accordion>

      {/* Summary */}
      <Accordion
        id="summary"
        num="SUMMARY"
        icon="🏁"
        title="Session Summary"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-4 p-5 bg-green-50 border border-green-200 rounded-2xl">
          <span className="text-3xl flex-shrink-0">✅</span>
          <div>
            <p className="text-base font-bold text-green-800 mb-3">
              You now have everything you need to start your FSJ/BFD
              application.
            </p>
            <ul className="space-y-2">
              {[
                "Documents: Motivationsschreiben, Lebenslauf, certificates, passport — all PDFs",
                "Motivationsschreiben: 5 paragraphs, specific, personal, max 1 page in German",
                "Email: short, professional, formal German — 5–8 lines only",
                "Träger: Caritas, Diakonie, AWO, DRK, city Träger, DJH — apply directly",
                "Best platforms: freiwillig-ja.de, bundesfreiwilligendienst.de, direct websites",
                "Accommodation-included placements are strongly recommended for international applicants",
                "Apply 6–12 months in advance — do not wait",
                "Target smaller cities for higher acceptance rates",
                "B1 certificate + personalized letter = your biggest competitive advantage",
                "Follow up by phone 1 week after sending your application",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5 font-bold">›</span>{" "}
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Tasks */}
      <Accordion
        id="tasks"
        num="TASKS"
        icon="📋"
        title="Your Tasks — Session 03"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>Start your Motivationsschreiben draft</strong> — Write
                all 5 paragraphs using the structure from this session. Use
                DeepL to help with German, then ask a native speaker to review.
              </>,
              <>
                <strong>Research 5 specific organizations</strong> — Find 5 real
                Träger (Caritas, Diakonie, AWO, DRK, or city Träger) in cities
                you are considering. Note their FSJ/BFD contact email and
                application deadline.
              </>,
              <>
                <strong>Check freiwillig-ja.de</strong> — Search for open
                placements in 2 different states. Filter by "Unterkunft
                inklusive" and note 3 placements that interest you.
              </>,
              <>
                <strong>Prepare your document checklist</strong> — Identify
                which documents you already have and which still need to be
                obtained, translated, or certified.
              </>,
              <>
                <strong>Accommodation decision</strong> — Based on this
                session's budget breakdown, decide whether you will target
                placement with or without accommodation, and which cities match
                that decision.
              </>,
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default FSJLecture03Application;
